/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  AlertProps,
  Box,
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GetSingleOrderDetailsObject } from "../../../@types/DRO/PurchaseOrderLinesResponse";
import { GetAllApproverListApi } from "../../../api/GetApproversList/GetAllApproverListApi";
import { ProductOrderApi } from "../../../api/ProductOrder/ProductOrderApi";
import { useLoading } from "../../../views/Loader/LoadingContext";

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref
) {
  return <MuiAlert elevation={6} ref={ref} {...props} />;
});
const ViewApprovalList: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setLoading } = useLoading();
  const [orderData, setOrderData] =
    useState<GetSingleOrderDetailsObject | null>(null);
  const [loadingError, setLoadingError] = useState(false);
  const [items, setItems] = useState<
    Array<{
      description: string;
      quantity: string;
      unit: string;
      price: string;
      taxes: string;
      total: string;
      unitPrice: string;
    }>
  >([]);

  const [selectedVendor, setSelectedVendor] = useState("");
  const [selectedGSTTreatment, setSelectedGSTTreatment] = useState("");
  const [selectedWarehouse, setSelectedWarehouse] = useState("");
  const [selectedApprovers, setSelectedApprovers] = useState<string[]>([]);
  const [orderDeadline, setOrderDeadline] = useState("");
  const [orderType, setOrderType] = useState("OPEN");
  const [orderStatus, setOrderStatus] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error"
  >();

  useEffect(() => {
    const fetchOrderData = async () => {
      if (!id) {
        setLoadingError(true);
        return;
      }

      setLoading(true);
      try {
        const response = await ProductOrderApi.api.getSingleProductOrderDetails(
          parseInt(id)
        );
        const purchaseOrderLines = response.data.purchaseOrderLines || [];

        // Transform the order data
        const transformedData = {
          ...response.data,
          purchaseOrderLines: purchaseOrderLines.map((line) => ({
            ...line,
            part: {
              id: line.partId,
              description: line.partName || "Unknown Part",
              price: line.price,
            },
          })),
        };

        setOrderData(transformedData);

        // Set items from the API response
        const formattedItems = purchaseOrderLines.map((line: any) => ({
          description: line.partName || "",
          quantity: line.quantityOrdered?.toString() || "",
          unit: line.unit || "Units",
          unitPrice: line.unitPrice || "0.00",
          price: `₹ ${line.price?.toFixed(2) || "0.00"}`,
          taxes: line.tax,
          total: `₹ ${
            (line.price * line.quantityOrdered)?.toFixed(2) || "0.00"
          }`,
        }));

        if (formattedItems.length > 0) {
          setItems(formattedItems);
        }

        // Set other fields from the API response
        setSelectedVendor(transformedData.vendorDetails?.vendorName || "");
        setSelectedGSTTreatment(transformedData.gstTreatment || "");
        setSelectedWarehouse(
          transformedData?.deliveryWareHouseDetails?.deliveryWarehouseName || ""
        );
        const formattedDate = transformedData.expectedArrival
          ? new Date(transformedData.expectedArrival).toISOString().slice(0, 16)
          : "";
        setOrderDeadline(formattedDate);
        setOrderType(transformedData.purchaseOrderType || "OPEN");
        setOrderStatus(transformedData.status?.name || "");

        // Set approvers
        if (
          transformedData.approvals &&
          Array.isArray(transformedData.approvals)
        ) {
          setSelectedApprovers(
            transformedData.approvals.map((approver: any) => approver.name)
          );
        }
      } catch (error) {
        console.error("Error fetching order details:", error);
        setLoadingError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderData();
  }, [id, setLoading]);

  const getStatusClass = (status: string): string => {
    const statusClasses: Record<string, string> = {
      DRAFT: "bg-gray-400",
      PENDING: "bg-yellow-400",
      APPROVED: "bg-green-400",
      REJECTED: "bg-red-400",
    };
    return statusClasses[status] || "bg-gray-400";
  };

  const handleBack = () => {
    navigate("/approval-list");
  };

  const handleApprove = async () => {
    if (!id) return;

    setLoading(true);
    try {
      await GetAllApproverListApi.api.approvePurchaseOrder(parseInt(id));
      setSnackbarOpen(true);
      setSnackbarMessage("Order approved successfully");
      setSnackbarSeverity("success");
      setTimeout(() => {
        navigate("/approval-list");
      }, 3000);
    } catch (error) {
      console.error("Error approving order:", error);
      setSnackbarOpen(true);
      setSnackbarMessage("Error approving order");
      setSnackbarSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!id) return;

    setLoading(true);
    try {
      await GetAllApproverListApi.api.rejectPurchaseOrder(parseInt(id));
      setSnackbarOpen(true);
      setSnackbarMessage("Order rejected successfully");
      setSnackbarSeverity("success");
      setTimeout(() => {
        navigate("/approval-list");
      }, 3000);
    } catch (error) {
      console.error("Error rejecting order:", error);
      setSnackbarOpen(true);
      setSnackbarMessage("Error rejecting order");
      setSnackbarSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  const handleSendForReview = async () => {
    if (!id) return;

    setLoading(true);
    try {
      await GetAllApproverListApi.api.sendBackToApproval(parseInt(id));
      setSnackbarOpen(true);
      setSnackbarMessage("Order sent for review successfully");
      setSnackbarSeverity("success");
      setTimeout(() => {
        navigate("/approval-list");
      }, 3000);
    } catch (error) {
      console.error("Error sending order for review:", error);
      setSnackbarOpen(true);
      setSnackbarMessage("Error sending order for review");
      setSnackbarSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  if (loadingError) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Error loading order</h2>
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
            Back to Approvals
          </button>
        </div>
      </div>
    );
  }

  if (!orderData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <Box sx={{ padding: 4 }}>
      {/* Heading */}
      <Typography
        variant="h5"
        gutterBottom
        sx={{ textAlign: "left", fontWeight: "500", color: "#1E1E1E", mb: 2 }}>
        Approval Request Details
      </Typography>

      {/* Breadcrumbs */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 4,
          backgroundColor: "#F8F9FA",
          padding: "12px 16px",
          borderRadius: "8px",
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
        }}>
        <Button
          onClick={handleBack}
          sx={{
            textTransform: "none",
            color: "#646464",
            p: 0,
            "&:hover": { color: "#1E1E1E", backgroundColor: "transparent" },
          }}>
          Dashboard
        </Button>
        <Typography variant="body2" color="#646464" sx={{ mx: 1 }}>
          &gt;
        </Typography>
        <Button
          onClick={handleBack}
          sx={{
            textTransform: "none",
            color: "#646464",
            p: 0,
            "&:hover": { color: "#1E1E1E", backgroundColor: "transparent" },
          }}>
          Approval Requests
        </Button>
        <Typography variant="body2" color="#646464" sx={{ mx: 1 }}>
          &gt;
        </Typography>
        <Typography variant="body2" color="#1E1E1E" sx={{ fontWeight: "500" }}>
          Review Request
        </Typography>
      </Box>

      {/* Status and Order Info */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: "600" }}>
            Order #{orderData?.id || "N/A"}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Created:{" "}
            {orderData?.confirmationDate
              ? new Date(orderData.confirmationDate).toLocaleDateString()
              : "N/A"}
          </Typography>
        </Box>
        <div
          className={`text-white font-semibold rounded-xl px-2 ${getStatusClass(
            orderData.status?.name
          )}`}>
          {orderData.status?.name || "N/A"}
        </div>
      </Box>

      {/* Form */}
      <Paper
        sx={{
          padding: 3,
          marginTop: 2,
          borderRadius: "8px",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
        }}>
        <Grid container spacing={3} sx={{ textAlign: "left" }}>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Vendor
            </Typography>
            <TextField
              fullWidth
              variant="standard"
              disabled
              value={selectedVendor}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              GST Treatment
            </Typography>
            <TextField
              fullWidth
              value={selectedGSTTreatment}
              variant="standard"
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Deliver To
            </Typography>
            <TextField
              fullWidth
              value={selectedWarehouse}
              variant="standard"
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Approval By
            </Typography>
            <TextField
              fullWidth
              value={selectedApprovers.join(", ")}
              variant="standard"
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Order Deadline
            </Typography>
            <TextField
              fullWidth
              value={
                orderDeadline ? new Date(orderDeadline).toLocaleString() : ""
              }
              variant="standard"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Order Type
            </Typography>
            <TextField
              fullWidth
              value={orderType === "OPEN" ? "Open Order" : "Close Order"}
              variant="standard"
              disabled
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Items Table */}
      <Box sx={{ marginTop: 4 }}>
        <Typography
          variant="h6"
          sx={{
            textAlign: "left",
            fontWeight: "600",
            color: "#1E1E1E",
            mb: 2,
          }}>
          Items
        </Typography>
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: "8px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
          }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#F8F9FA" }}>
                <TableCell sx={{ fontWeight: "600" }}>#</TableCell>
                <TableCell sx={{ fontWeight: "600", width: "300px" }}>
                  Product Description
                </TableCell>
                <TableCell sx={{ fontWeight: "600" }}>Quantity</TableCell>
                <TableCell sx={{ fontWeight: "600" }}>
                  Unit Of Measure
                </TableCell>
                <TableCell sx={{ fontWeight: "600" }}>Unit Price</TableCell>
                <TableCell sx={{ fontWeight: "600" }}>Tax</TableCell>
                <TableCell sx={{ fontWeight: "600" }}>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{item.description}</TableCell>
                  <TableCell>{item.quantity}</TableCell>
                  <TableCell>{item.unit}</TableCell>
                  <TableCell>{item.unitPrice}</TableCell>
                  <TableCell>{item.taxes}</TableCell>
                  <TableCell>{item.price}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ marginTop: 4, textAlign: "right" }}>
        <Box>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleBack}
            sx={{
              marginRight: 2,
              padding: "10px 20px",
              borderRadius: "32px",
              textTransform: "none",
              fontWeight: "500",
            }}>
            Back to Approvals
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSendForReview}
            sx={{
              marginRight: 2,
              padding: "10px 20px",
              borderRadius: "32px",
              textTransform: "none",
              fontWeight: "500",
            }}>
            Send for Review
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            sx={{
              marginRight: 2,
              padding: "10px 20px",
              borderRadius: "32px",
              textTransform: "none",
              fontWeight: "500",
            }}>
            Reject
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApprove}
            sx={{
              padding: "10px 20px",
              borderRadius: "32px",
              textTransform: "none",
              fontWeight: "500",
            }}>
            Approve
          </Button>
        </Box>
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ViewApprovalList;
